import SnwButtonContainer from "@@/components/Button/SnwButtonContainer";
import { SnwButtonType } from "@@/components/Button/buttonProps";
import ServiceDescriptionCard, {
  ServiceDescriptionCardProps,
} from "@@/components/Card/Service-description";
import Box from "@@/elements/Box";
import Container from "@@/elements/Container";
import Typography from "@@/elements/Typography";
import { ScreenSize, useIsBreakpointAndAbove, useIsBreakpointOrBelow } from "@@/hooks/device/use-breakpoint";
import EpiProperty from "@@/integrations/optimizly/Property";

import OptimizelyBlock, { OptimizelyBlockProps } from "@@/integrations/optimizly/Block";
import styles from "../cardContainer.module.scss";

export type ServiceDescriptionContainerProperties = {
  header?: string;
  intro?: string;
  headerLabel?: string;
  buttons?: SnwButtonType[];
  cards: ServiceDescriptionCardProps[];
};

export type ServiceDescriptionContainerOwnProps = {
  backgroundColor: BackgroundColor;
  showListItemsText: string;
  hideListItemsText: string;
  headerVariant: "center" | "left";
};

export type BackgroundColor = "beige" | "white" | "blue" | "green";

export type ServiceDescriptionContainerProps =
  ServiceDescriptionContainerProperties & ServiceDescriptionContainerOwnProps;

export const colorMapper = (color) => {
  switch (color) {
  case "beige":
    return "ComplementaryBeigeTint04";
  case "white":
    return "White";
  case "blue":
    return "PrimaryBlueTint03";
  case "green":
    return "PrimaryGreenTint03";
  default:
    return "ComplementaryBeigeTint04";
  }
};

export const ServiceDescriptionContainer = ({
  backgroundColor,
  headerLabel,
  header,
  intro,
  buttons,
  cards,
  showListItemsText,
  hideListItemsText,
  headerVariant = "center",
}: ServiceDescriptionContainerProps) => {
  const isExtraLarge = useIsBreakpointAndAbove(ScreenSize.EXTRA_LARGE);
  const isSmall = useIsBreakpointOrBelow(ScreenSize.SMALL);
  const useLeftHeader = (headerVariant === "left" && cards.length < 3 && isExtraLarge);

  const renderHeaderContent = () => {
    return (
      <>
        {headerLabel && headerVariant === "left" && (
          <Box className={styles.service_container_label}>
            <EpiProperty<ServiceDescriptionContainerProperties> name="headerLabel">
              <Typography
                variant="pretitle"
                asElement="div">
                {headerLabel}
              </Typography>
              <hr />
            </EpiProperty>
          </Box>
        )}
        {header && (
          <EpiProperty<ServiceDescriptionContainerProperties> name="header">
            <Typography
              asElement="h2"
              variant="heading1"
              textAlign={headerVariant === "left" ? "left" : "center"}>
              {header}
            </Typography>
          </EpiProperty>
        )}
        {intro && (
          <EpiProperty<ServiceDescriptionContainerProperties> name="intro">
            <Typography
              variant="body"
              textAlign={headerVariant === "left" ? "left" : "center"}
              mt={2}>
              {intro}
            </Typography>
          </EpiProperty>
        )}
        {useLeftHeader && buttons && buttons.length > 0 && (
          <EpiProperty<ServiceDescriptionContainerProperties> name="buttons">
            <Box
              mt={10}
              width={"full"}
              justifyContent={headerVariant === "left" ? "flex-start" : "center"}
              flexDirection={["column", "row", "row"]}
              gap={6}>
              <SnwButtonContainer buttons={buttons} />
            </Box>
          </EpiProperty>
        )}
      </>);
  };

  if (useLeftHeader) {
    return (
      <Box
        component="section"
        backgroundColor={colorMapper(backgroundColor)}
        className={styles.block}
      >
        <Container component="div">
          <Box className={styles.service_container}>
            <Box className={styles.left_header_container}>
              {renderHeaderContent()}
            </Box>
            {cards?.map((card, i) => (
              <ServiceDescriptionCard
                key={i}
                {...card}
                backgroundColor={backgroundColor === "white" ? "beige" : "white"}
                collapsListItems={isSmall}
                showListItemsText={showListItemsText}
                hideListItemsText={hideListItemsText}
              />
            ))}
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      component="section"
      backgroundColor={colorMapper(backgroundColor)}
      className={styles.block}
    >
      <Container component="div">
        <Box
          mb={[10, 12, 14]}
          width="full"
          flexDirection="column"
          alignItems={headerVariant === "left" ? "flex-start" : "center"}
        >
          {renderHeaderContent()}
        </Box>
        <EpiProperty<ServiceDescriptionContainerProperties> name="cards">
          <Box className={styles.service_container}>
            {cards?.map((card, i) => (
              <ServiceDescriptionCard
                key={i}
                {...card}
                backgroundColor={backgroundColor === "white" ? "beige" : "white"}
                collapsListItems={isSmall}
                showListItemsText={showListItemsText}
                hideListItemsText={hideListItemsText}
              />
            ))}
          </Box>
        </EpiProperty>
        {!useLeftHeader && (
          <EpiProperty<ServiceDescriptionContainerProperties> name="buttons">
            <Box
              mt={8}
              width={"full"}
              justifyContent={useLeftHeader ? "flex-start" : "center"}
              flexDirection={["column", "row", "row"]}
              gap={6}>
              <SnwButtonContainer buttons={buttons} />
            </Box>
          </EpiProperty>
        )}
      </Container>
    </Box>
  );
};
export type ProvidedServiceDescriptionContainerProps =
  OptimizelyBlockProps<ServiceDescriptionContainerProps> & ServiceDescriptionContainerProps;

const ProvidedTextMediaBlockCard = ({
  blockId,
  inEditMode,
  properties,
  ...rest
}: ProvidedServiceDescriptionContainerProps) => (
  <OptimizelyBlock
    blockId={blockId}
    inEditMode={inEditMode}
    properties={properties}
  >
    {(optimizlyProperties) => (
      <ServiceDescriptionContainer
        {...rest}
        {...optimizlyProperties} />
    )}
  </OptimizelyBlock>
);

export default ProvidedTextMediaBlockCard;